import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import colors from '../../../../constants/colors';
import stretchBuilder from '../../../../utils/animations';
import InternalLink from '../../../InternalLink';
import Back from '../../../Back';
import { labelText, touchableText } from '../../../../constants/globalStyles';

const Wrapper = styled.main`
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  margin: 105px auto;
  width: 990px;
  position: relative,
  z-index: 0;

  .simplebar-wrapper {
    min-height: 150px;
    max-height: 480px;

    @media screen and (max-width: 992px) {
      height: calc(100% + 10px) !important;
      max-height: 100%;
      min-height: 100%;
      overflow: auto;
    }
  }

  @media screen and (max-width: 992px) {
    margin: 0 auto;
    width: 100%;
  }
`;

const Section = styled.section`
  background: ${colors.main.white};
  border-radius: 2px;
  position: relative;
  margin-bottom: 10px;
  padding: 40px 0;

  transition: all 0.3s;
  overflow: hidden;
  width: 100%;

  ${(props) =>
    props.less &&
    `
    padding: 0;
    margin: 0;
    background: none;
  `}

  ${(props) =>
    props.left &&
    `
    width: calc(50% - 5px);
    margin-right: 5px;
    min-height: 575px;
  `}

  ${(props) =>
    props.right &&
    `
    width: calc(50% - 5px);
    margin-left: 5px;
    min-height: 575px;
  `}

  @media screen and (max-width: 992px) {
    ${(props) =>
      props.first &&
      `
      padding-top: 0;
    `}

    ${(props) =>
      !props.first &&
      `
      min-height: auto;
      max-height: 100%;
      width: 100%;
    `}

    ${(props) =>
      props.left &&
      `
      order: 2;
    `}

    ${(props) =>
      props.right &&
      `
      order: 1;
    `}
  }
`;

const SectionHeader = styled.div`
  padding: 0 32px;

  @media screen and (max-width: 992px) {
    padding: 0 calc((100vw - 89.333%) / 2);
  }
`;

const ProgramImage = styled.img`
  height: 100%;
  width: 100%;
  filter: brightness(0.8);
  object-fit: cover;
`;

const Information = styled.div`
  align-items: flex-end;
  background: transparent;
  display: flex;
  flex-wrap: wrap;
  position: absolute;
  left: 15px;
  bottom: 25px;
  width: 95%;
  z-index: 100;

  @media screen and (max-width: 992px) {
    left: calc((100% - 89.333%) / 2);
    right: calc((100% - 89.333%) / 2);
    width: 250px;
  }
`;

const InformationTitle = styled.div`
  transform: translateX(-2px);
  max-height: 100px;
  margin-bottom: 8px;
  overflow: hidden;
  width: 100%;
`;

const Program = styled.div`
  border-radius: 2px;
  overflow: hidden;
  display: block;
  background: ${colors.main.dividers} 50% 50% no-repeat;
  background-size: cover;
  position: relative;
  height: 523px;
  margin-bottom: 22px;
  width: 100%;

  &:before {
    content: '';
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    opacity: 0.5;
    position: absolute;
    transition: all 5.5s;
    z-index: 1;

    ${(props) => {
      if (props.isLooping) {
        return css`
          background-color: ${rgba(colors.main.lightBlue, 0.7)};
          background: linear-gradient(
            35deg,
            ${rgba(colors.main.ruby, 0.5)} 16%,
            ${rgba(colors.main.blue, 0.5)} 64%
          );
          opacity: 1;
        `;
      }
      if (props.sessionsDaysProgress) {
        return css`
          animation: ${stretchBuilder(props.sessionsDaysProgress)} 2s ease-in-out;
          animation-fill-mode: forwards;
          background-color: ${rgba(colors.main.lightBlue, 0.7)};
          background: linear-gradient(
            35deg,
            ${rgba(colors.main.ruby, 0.5)} 16%,
            ${rgba(colors.main.blue, 0.5)} 64%
          );
          opacity: 1;
          right: initial;
        `;
      }
      return css`
        background: linear-gradient(
          35deg,
          ${rgba(colors.main.ruby, 0.5)} 16%,
          ${rgba(colors.main.blue, 0.5)} 64%
        );
      `;
    }}
  }

  @media screen and (max-width: 992px) {
    height: 250px;
    position: absolute;
    top: 0;
    left: calc((-100vw + 100%) / 2);
    right: calc((-100vw + 100%) / 2);
  }

  @media screen and (max-width: 992px) and (orientation: landscape) {
    height: 300px;
  }

  @media screen and (min-width: 765px) and (max-width: 992px) and (orientation: portrait) {
    height: 500px;
  }
`;

// Details block for each purchased program
const Details = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
  width: 100%;

  @media screen and (max-width: 992px) {
    flex-wrap: wrap;
    margin-top: 270px;
  }

  @media screen and (max-width: 992px) and (orientation: landscape) {
    margin-top: 310px;
  }

  @media screen and (min-width: 765px) and (max-width: 992px) and (orientation: portrait) {
    margin-top: 510px;
  }
`;

const Tracks = styled.div`
  margin-top: 16px;

  ${(props) =>
    props.stacked
      ? css`
          display: grid;
          grid-template-columns: auto 1fr;
          align-items: center;
          gap: 12px 16px;
        `
      : css`
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        `}
`;

const TrackOption = styled.div`
  display: contents;
`;

const TrackHowTo = styled.div`
  p {
    margin: 0;
  }

  p + p {
    margin-top: 8px;
  }
`;

const Pill = styled.button`
  ${touchableText};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 18px;
  border: 2px solid ${colors.main.black};
  border-radius: 4px;
  font-size: 12px;
  background: ${(props) => (props.selected ? colors.main.black : 'transparent')};
  color: ${(props) => (props.selected ? colors.main.white : colors.main.black)};
  cursor: ${(props) => (props.selected ? 'default' : 'pointer')};
  transition:
    background 0.2s ease,
    color 0.2s ease;

  ${(props) =>
    !props.selected &&
    css`
      &:hover {
        background: ${colors.main.dividers};
      }
    `}

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }
`;

const RestartButton = styled(Pill)`
  width: 100%;
  height: 44px;
  margin-top: 28px;
`;

const Summary = styled.div`
  flex: 1;
  max-width: 520px;
  margin-right: 40px;

  @media screen and (max-width: 992px) {
    margin-right: 0;
    margin-bottom: 24px;
    max-width: 100%;
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 380px;
  flex-shrink: 0;

  @media screen and (max-width: 992px) {
    width: 100%;
  }
`;

const InfoGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 28px 20px;
  padding-bottom: 28px;
  margin-bottom: 28px;
  border-bottom: 1px solid ${colors.main.dividers};
`;

const StatValue = styled.div`
  font-family: 'DINNextW01-Condensed', Arial, sans-serif;
  font-size: 36px;
  font-weight: 300;
  line-height: 1;
  color: ${colors.main.fandango};
`;

const InfoLabel = styled.div`
  ${labelText};
  margin-top: 6px;
  color: ${(props) => (props.dark ? colors.main.black : colors.main.grey)};
`;

const SectionTitle = styled.h3`
  ${labelText};
  margin: 0;
  color: ${colors.main.black};
`;

const RightSection = styled.div`
  position: relative;

  & + & {
    border-top: 1px solid ${colors.main.dividers};
    margin-top: 28px;
    padding-top: 28px;
  }
`;

const StyledInternalLink = styled(InternalLink)``;

const StyledBack = styled(Back)`
  display: none;
  position: absolute;
  top: 15px;
  z-index: 100;

  @media screen and (max-width: 992px) {
    display: block;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  position: relative;
`;

export {
  StyledBack,
  Wrapper,
  Section,
  SectionHeader,
  Program,
  ProgramImage,
  Details,
  Tracks,
  TrackOption,
  Pill,
  TrackHowTo,
  Summary,
  RightColumn,
  RightSection,
  InfoGroup,
  InfoLabel,
  StatValue,
  SectionTitle,
  RestartButton,
  StyledInternalLink,
  Information,
  InformationTitle,
  TitleWrapper,
};
